/* eslint-disable no-console */
import { appendParams, buildParams, parseURL, replaceUrlParam } from '../../../global/js/url';
import isJson from '../../../global/js/validateJSON';
import React, { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import useCookies from '../config/UseCookies';
import localStorageReducer from '../reducers/localStorageReducer';
import FunnnelbackContext from './FunnelbackContext';

/**
 * Generate pagination range.
 *
 * @param {number} totalMatching Total number of results.
 * @param {string} numRanks      How many results per page.
 * @param {number} currentPage   Current page.
 * @param {number} numPages      How many pages we want to include in pagination.
 *
 * @return {number[]}
 */
export function generatePageRange(totalMatching, numRanks, currentPage, numPages) {
    if (totalMatching === 0) {
        return [];
    }
    if (totalMatching <= numRanks) {
        return [1];
    }

    const allPages = Math.floor((totalMatching + numRanks) / numRanks);
    const firstPage = currentPage > Math.floor(numPages / 2) ? currentPage - Math.floor(numPages / 2) : 1;
    const lastPage = allPages > firstPage + numPages ? firstPage + numPages : allPages + 1;

    const range = (start, stop) => Array.from({ length: stop - start }, (_, i) => start + i);

    const pages = range(firstPage, lastPage);

    return pages;
}

/**
 * Validate user data for the range of number of results to display in SearchResultsToDisplay Component.
 * @param {string} numberString String of data entered by the user. Each number should be separated by a comma.
 * @returns {string[]}
 */
function validateNumberOfResultsToDisplay(numberString) {
    const arr = numberString.split(',');
    let err = false;

    // Validate the data entered by the user.
    const validateUserData = arr.map((item) => {
        const newString = item.trim();
        const num = +newString;
        // eslint-disable-next-line no-restricted-globals
        if (isNaN(num)) {
            console.error('Funnelback Instant Search - You must enter a valid number value in your range of search results to display.');
            err = true;
        }
        if (num < 0) {
            console.error("Funnelback Instant Search - You cannon't enter a negative number in your range of search results to display.");
            err = true;
        }
        return num;
    });

    // If we encounter any errors with the data we return nothing to the frontend.
    if (err) {
        return [];
    }

    // If no errors, turn back into an array of strings to render out on the frontend.
    const resultsToStringArr = validateUserData.map((num) => {
        return `${num}`;
    });

    return resultsToStringArr;
}

/**
 *
 * @param {object} defaultSortByOptions Object of default key/value paired options EG > "relevance": true
 * @returns {string[]}
 */
function prepareDefaultSortByOptions(defaultSortByOptions) {
    // Convert obj into an array
    const turnObjectIntoArray = Object.entries(defaultSortByOptions);
    // If user has marked any default values as false, remove from array
    // eslint-disable-next-line no-unused-vars
    const removeFalsyValues = turnObjectIntoArray.filter(([_, flag]) => flag === true);
    // Destructure and return an array of [title, value] arrays for UI consumption
    const newArr = removeFalsyValues.map(([first]) => {
        switch (first) {
            case 'relevance':
                return ['Relevance', first];
            case 'date':
                return ['Date (Newest First)', first];
            case 'adate':
                return ['Date (Oldest First)', first];
            case 'title':
                return ['Title (A-Z)', first];
            case 'dtitle':
                return ['Title (Z-A)', first];
            case 'prox':
                return ['Geo (Nearest first)', first];
            case 'dprox':
                return ['Geo (Furthest first)', first];
            case 'url':
                return ['URL (0-9, A-Z)', first];
            case 'durl':
                return ['URL (Z-A, 9-0)', first];
            case 'size':
                return ['File size (Largest first)', first];
            case 'dsize':
                return ['File size (Smallest first)', first];
            default:
                return [];
        }
    });
    return newArr;
}

/**
 * Validate / Prepare Advanced Sort By Options
 * @param {object} obj Object of user entered key value pairs that they wish to use as sort by options.
 * @returns {string[]}
 */
function prepareAdvancedSortByOptions(obj) {
    const validJSON = isJson(obj);
    if (!validJSON) {
        console.error('Funnelback Instant Search - Your advanced sort by options must be in valid JSON format.');
        return {};
    }
    const parseJSON = JSON.parse(obj);
    const array = Object.entries(parseJSON);
    return array;
}

/**
 * Data Fetching and props distribution.
 *
 * @param defaultParams
 * @param children
 * @param defaultSortByOptions
 * @param advancedSortByOptions
 * @returns {JSX.Element}
 * @constructor
 */
function FunnelbackProvider({
    defaultParams,
    children,
    displayMode,
    numRank,
    defaultSortByOptions,
    advancedSortByOptions,
    favourites,
    comparisons,
}) {
    const [searchTerm, setSearchTerm] = useState(null);
    const [isFetching, setFetching] = useState(false);
    const [bestBets, setBestBets] = useState([]);
    const [results, setResults] = useState([]);
    const [facets, setFacets] = useState([]);
    const [facetsMap, setFacetsMap] = useState([]);
    const [error, setError] = useState('');
    const [params, setParams] = useState({});
    const [queryString, setQueryString] = useState(null);
    const [resultsSummary, setResultSummary] = useState({});
    const [collection, setCollection] = useState(null);
    const [userDisplayMode, setUserDisplayMode] = useState(displayMode);
    const [qsups, setQsups] = useState([]);
    const validatedNumberOfResultsToDisplayData = validateNumberOfResultsToDisplay(numRank);
    const [numberOfSearchResultsToDisplay, setNumberOfSearchResultsToDisplay] = useState('');
    const [saveToCookie, readCookie] = useCookies();
    const [activeTab, setActiveTab] = useState();
    const [selectedFacets, setSelectedFacets] = useState();

    useEffect(() => {
        const numResultsToDisplayCookie = readCookie('numRank');
        // If a cookie exists, set its value to be the initialState
        if (numResultsToDisplayCookie) {
            setNumberOfSearchResultsToDisplay(numResultsToDisplayCookie);
        } else {
            // Use the first input value in the array to set the default number of results to display to users.
            setNumberOfSearchResultsToDisplay(validatedNumberOfResultsToDisplayData[0]);
        }
    }, []);

    useEffect(() => {
        saveToCookie('numRank', numberOfSearchResultsToDisplay);
    }, [numberOfSearchResultsToDisplay]);

    const url = defaultParams?.serviceUrl;

    const headers = {
        'content-type': 'application/json',
    };

    const [sortByFilter, setSortByFilter] = useState('');
    const advancedSortByFilterOptions = prepareAdvancedSortByOptions(JSON.stringify(advancedSortByOptions));
    const defaultSortByFilterOptions = prepareDefaultSortByOptions(defaultSortByOptions);

    // If there are advanced options, be sure that we automatically load the first sort option in the params.
    // Else use the default options (relevance = "")
    useEffect(() => {
        if (advancedSortByFilterOptions.length) {
            setSortByFilter(advancedSortByFilterOptions[0][0]);
        } else {
            setSortByFilter('');
        }
    }, []);

    const [sortByFilterOption, setSortByFilterOption] = useState({ sort: sortByFilter });
    useMemo(() => {
        setSortByFilterOption({ sort: sortByFilter });
    }, [sortByFilter]);

    const initialLocalStorageState = {
        favourites:
            JSON.parse(localStorage.getItem('FB-Instant-Search-Favourites')) !== null
                ? JSON.parse(localStorage.getItem('FB-Instant-Search-Favourites'))
                : [],
        comparisons:
            JSON.parse(localStorage.getItem('FB-Instant-Search-Comparisons')) !== null
                ? JSON.parse(localStorage.getItem('FB-Instant-Search-Comparisons'))
                : [],
    };

    const [localStorageState, dispatchLocalStorage] = useReducer(localStorageReducer, initialLocalStorageState);
    const addFavourite = (payload) => dispatchLocalStorage({ type: 'addSingleFavourite', payload });
    const resetSingleFavourite = (payload) => dispatchLocalStorage({ type: 'removeSingleFavourite', payload });
    const resetFavourites = () => dispatchLocalStorage({ type: 'clearAllFavourites' });
    const addComparison = (payload) => dispatchLocalStorage({ type: 'addSingleComparison', payload });
    const resetSingleComparison = (payload) => dispatchLocalStorage({ type: 'removeSingleComparison', payload });
    const resetComparisons = () => dispatchLocalStorage({ type: 'clearAllComparisons' });

    // Update application local storage to be aligned with the state.
    useEffect(() => {
        localStorage.setItem('FB-Instant-Search-Favourites', JSON.stringify(localStorageState.favourites));
        localStorage.setItem('FB-Instant-Search-Comparisons', JSON.stringify(localStorageState.comparisons));
    }, [localStorageState.favourites, localStorageState.comparisons]);

    const fetchResults = useCallback(
        (event, queryParams, signal) => {
            if (event) {
                event.preventDefault();
            }

            setFetching(true);
            // Either direct state stored queryString or passed directly into fetcher.
            const endpoint = appendParams(url, queryParams || queryString);

            fetch(endpoint, {
                method: 'GET',
                headers,
                signal,
            })
                .then((response) => {
                    return response.json();
                })
                .then((_results) => {
                    const totalMatching = _results?.response?.resultPacket?.resultsSummary?.totalMatching;
                    const numRanks = +numberOfSearchResultsToDisplay;
                    const currStart = _results?.response?.resultPacket?.resultsSummary?.currStart;
                    const currentPage = currStart > 0 && numRanks > 0 ? Math.floor((currStart + numRanks) / numRanks) : 1;

                    const pages = generatePageRange(totalMatching, numRanks, currentPage, defaultParams.numPages);

                    setFetching(false);
                    setBestBets(_results?.response?.curator?.exhibits || []);
                    setResults(_results?.response?.resultPacket?.results);
                    setResultSummary({
                        ..._results?.response?.resultPacket?.resultsSummary,
                        query: _results?.question?.query,
                        pages,
                        currentPage,
                        numRanks,
                    });
                    setQsups(_results?.response?.resultPacket?.qsups);
                    window.history.scrollRestoration = 'manual';
                    window.history.pushState({ query: searchTerm, num_ranks: numRanks }, '', queryString);
                    setParams({ ...parseURL(queryParams) });

                    setFacets(_results?.response?.facets);

                    const resultsTabs = _results?.response?.facets.filter((facet) => facet.guessedDisplayType === 'TAB')[0];
                    const selectedTab = resultsTabs?.allValues.filter((tab) => tab.selected)[0];
                    setActiveTab({ [`f.Tabs|${selectedTab.data}`]: selectedTab.label });

                    setSelectedFacets(_results?.question?.selectedCategoryValues || {});
                    setFacetsMap(_results?.question?.collection?.facetedNavigationConfConfig?.facetDefinitions || []);
                })
                .catch((e) => {
                    console.log(e);
                    setFetching(false);
                    setBestBets([]);
                    setResults([]);
                    setError(e.message);
                });
        },
        [queryString, numberOfSearchResultsToDisplay]
    );

    useEffect(() => {
        if (!defaultParams || defaultParams?.serviceUrl === '') {
            setError(`Missing serviceUrl or collection parameter.`);
            return;
        }

        // Create the number of results to display param to pass to the fetch
        const numResults = { num_ranks: +numberOfSearchResultsToDisplay };

        const urlParams = parseURL(window.location.search);

        const { serviceUrl, ...rest } = defaultParams;

        const mergedParams = rest
            ? { ...urlParams, ...rest, collection: defaultParams.collection }
            : { ...urlParams, collection: defaultParams.collection };

        setParams({ ...mergedParams, ...numResults, ...sortByFilterOption, ...activeTab, ...selectedFacets });
        setCollection(mergedParams.collection);

        const queryParams = buildParams(
            { ...mergedParams, ...numResults, ...sortByFilterOption, ...activeTab, ...selectedFacets },
            true,
            true
        );

        if (mergedParams.query && mergedParams.query !== '') {
            setSearchTerm(mergedParams.query);
            fetchResults(null, queryParams);
        }
    }, [defaultParams, numberOfSearchResultsToDisplay, sortByFilter]);

    useEffect(() => {
        if (searchTerm !== null || collection !== null || url !== null) {
            setQueryString(() => buildParams({ ...params, query: searchTerm }, true, true));
        }
    }, [searchTerm]);

    const onKeyUp = (event) => {
        event.preventDefault();
        setSearchTerm(event.target.value);
    };

    const onFocus = (event) => {
        event.preventDefault();
        setSearchTerm(event.target.value);
    };

    const updateParams = (param) => {
        return (event) => {
            event.preventDefault();
            // We need to ensure that the queryString is updated with the latest piece of state
            const updateNumRank = replaceUrlParam(window.location.search, { num_ranks: +numberOfSearchResultsToDisplay });
            const queryParams = replaceUrlParam(updateNumRank, param);
            setQueryString(queryParams);
            fetchResults(null, queryParams);
        };
    };

    const memoValue = useMemo(() => ({
        fetchResults,
        onKeyUp,
        onFocus,
        updateParams,
        isFetching: defaultParams?.isFetching || isFetching,
        bestBets,
        results,
        facets,
        facetsMap,
        searchTerm,
        setSearchTerm,
        error,
        resultsSummary,
        queryString,
        setQueryString,
        collection,
        userDisplayMode,
        setUserDisplayMode,
        validatedNumberOfResultsToDisplayData,
        numberOfSearchResultsToDisplay,
        setNumberOfSearchResultsToDisplay,
        defaultSortByFilterOptions,
        sortByFilter,
        setSortByFilter,
        advancedSortByFilterOptions,
        localStorageState,
        addFavourite,
        resetSingleFavourite,
        resetFavourites,
        favourites,
        addComparison,
        resetSingleComparison,
        resetComparisons,
        comparisons,
        qsups,
        activeTab,
        setActiveTab,
        selectedFacets,
        setSelectedFacets,
    }));

    return <FunnnelbackContext.Provider value={memoValue}>{children}</FunnnelbackContext.Provider>;
}

export default FunnelbackProvider;
